import { KeyStatus } from "../types/stats";

import GameState from "./game-state";
import { IGame } from "../types/game";
import { IInputManager } from "../types/input-manager";
import StampBlockState from "./stamp-block-state";
import FirstFragState from "./first-frag-state";

export default class ProcessKeyState extends GameState {
  input: IInputManager;
  holdLeft: number;
  holdRight: number;
  bougeLeft: boolean;
  bougeRight: boolean;
  autoBouge: boolean;

  constructor(game: IGame) {
    super(game);

    this.holdLeft = this.holdRight = 0;
  }

  enter(): void {
    // Match original Player_process_key::init() (player.cc:424-432)
    // Original: canvas->clear_key(6); // drop
    // Original: if(!canvas->continuous) canvas->clear_key(3); // down
    // Original: canvas->start_moves();
    super.enter();
    
    if (this.game.input) {
      // Clear drop key (matches original clear_key(6))
      this.game.input.keyDrop = KeyStatus.NONE;
      // Clear down key (matches original clear_key(3))
      this.game.input.keyDown = KeyStatus.NONE;
    }
    
    // Start move accumulation (matches original canvas->start_moves())
    this.canvas.startMoves();
  }

  update(frameTime: number): void {
    super.update(frameTime);

    // Check for first frag in survivor mode (matches original player.cc:444-446)
    // This allows alive players to transition to first_frag state when syncpoint changes
    // Only check if player is actually alive (idle=1, not dead/waiting)
    const gameMode = this.game.getGameMode?.();
    if (gameMode === "survivor" && this.canvas.idle === 1) {
      if (this.check_first_frag()) {
        return; // Early return if transitioned to first_frag
      }
    }

    // Check if game is paused (matches original player.cc:447-448)
    if (this.game.paused) {
      // Log occasionally to verify pause state
      if (Math.random() < 0.01) { // 1% of the time
        console.log(`[ProcessKeyState.update] Game is paused, returning early: game.paused=${this.game.paused}, overmind.paused=${(this.game.overmind as any).paused}`);
      }
      return; // Don't move blocks if game is paused
    }

    // Check if block exists before processing moves
    if (!this.canvas.bloc) {
      this.game.popState(this);
      return;
    }

    // Start move byte accumulation (matches original line 451)
    this.canvas.startByte();

    // Handle input (may call canvas.setBit() if keys pressed)
    if (this.game.input) this.handleInput();
    
    // Write move byte (matches original line 460)
    this.canvas.writeByte();
    
    this.step();
  }

  /**
   * Check for first frag in survivor mode (matches original Player_process_key::check_first_frag() line 510-523)
   * If syncpoint is not PLAYING or LAST, transition to first_frag state
   * This should only be called for alive players (idle=1)
   */
  private check_first_frag(): boolean {
    const syncpoint = this.game.getSyncpoint?.();
    
    // If syncpoint is PLAYING or LAST, don't transition
    if (syncpoint === "playing" || syncpoint === "last") {
      return false;
    }

    // If syncing toward WAITFORWINNER or WAITFORRESTART, go to first_frag state
    // Send client first frag packet (matches original P_CLIENTFIRST_FRAG)
    if (this.game.sendClientFirstFrag) {
      this.game.sendClientFirstFrag();
    }

    this.call(new FirstFragState(this.game));
    return true;
  }

  private step(): void {
    if (!this.canvas.bloc) {
      this.game.popState(this);
      return;
    }
    this.moveBlockX();
    this.moveBlockY();
  }

  private moveBlockX(): void {
    if (!this.canvas.bloc) return;
    const targetX = ((this.canvas.bloc.x - 4) << 4) * 18;
    let newX: number = this.canvas.bloc.posX;
    if (newX < targetX) {
      newX += this.canvas.sideSpeed;
      if (newX > targetX) newX = targetX;
    }
    if (newX > targetX) {
      newX -= this.canvas.sideSpeed;
      if (newX < targetX) newX = targetX;
    }

    this.canvas.bloc.posX = newX;
  }

  private moveBlockY(): void {
    if (!this.canvas.bloc) return;
    const targetY = this.calcBy(this.canvas.bloc.posY + this.canvas.speed);
    const willCollide = this.canvas.checkCollide(
      this.canvas.bloc,
      this.canvas.bloc.x,
      targetY,
      this.canvas.bloc.rotation
    );
    
    if (willCollide) {
      // Clear keys before sending moves (matches original)
      if (this.game.input) {
        this.game.input.keyDrop = KeyStatus.NONE;
        this.game.input.keyDown = KeyStatus.NONE;
      }
      
      // Send accumulated moves before transitioning (matches original line 479)
      this.canvas.sendPMoves();
      
      // Send stamp packet BEFORE transitioning (matches original line 480-494)
      if (this.game.player.isLocal() && this.game.sendStampBlock) {
        this.game.sendStampBlock();
      }
      
      this.call(new StampBlockState(this.game));
      this.markDone();
      return;
    } else {
      this.canvas.bloc.posY += this.canvas.speed;
      // Match original: recalculate grid coordinate from pixel coordinate
      this.canvas.bloc.y = this.calcBy(this.canvas.bloc.posY);
      if (this.canvas.shadow) this.canvas.calcShadow(this.canvas.bloc);
    }
  }

  private handleInput(): void {
    let bougeLeft: boolean = false,
      bougeRight: boolean = false,
      autoBouge: boolean = false,
      input = this.game.input,
      block = this.canvas.bloc,
      canvas = this.canvas;

    if (!block) return;

    if (input.keyRotate & KeyStatus.RELEASED) {
      if (!this.rotateRight(false) && canvas.collideSideOnly) {
        let inc: number;
        if (block.x < 5) inc = 1;
        else inc = -1;
        if (
          !canvas.checkCollide(block, block.x + inc, block.y, block.rotation)
        ) {
          block.x += inc;
          if (this.rotateRight(false)) autoBouge = true;
          else block.x -= inc;
        }
      }
      input.keyRotate = KeyStatus.NONE;
    }

    if (input.keyRotateTwice & KeyStatus.RELEASED) {
      if (!this.rotateRight(true) && canvas.collideSideOnly) {
        let inc: number;
        if (block.x < 5) inc = 1;
        else inc = -1;
        if (
          !canvas.checkCollide(block, block.x + inc, block.y, block.rotation)
        ) {
          block.x += inc;
          if (this.rotateRight(true)) autoBouge = true;
          else block.x -= inc;
        }
      }
      input.keyRotateTwice = KeyStatus.NONE;
    }

    if (
      input.keyDown & KeyStatus.PRESSED ||
      input.keyDown & KeyStatus.RELEASED
    ) {
      this.moveDown();
      if (input.keyDown === KeyStatus.RELEASED) input.keyDown = KeyStatus.NONE;
    }

    if (input.keyDrop & KeyStatus.PRESSED) {
      this.dropDown();
      input.keyDrop = KeyStatus.NONE;
    }

    if (
      input.keyLeft & KeyStatus.PRESSED ||
      input.keyLeft & KeyStatus.RELEASED
    ) {
      if (!bougeLeft) {
        bougeLeft = true;
        if (input.keyLeft === KeyStatus.RELEASED)
          input.keyLeft = KeyStatus.NONE;
      }
    }
    if (
      input.keyRight & KeyStatus.PRESSED ||
      input.keyRight & KeyStatus.RELEASED
    ) {
      if (!bougeRight) {
        bougeRight = true;
        if (input.keyRight === KeyStatus.RELEASED)
          input.keyRight = KeyStatus.NONE;
      }
    }

    if (bougeLeft && !bougeRight && !autoBouge) {
      if (this.holdLeft < 2) {
        if (this.holdLeft === 0)
          this.holdLeft = canvas.horizontalRepeatDelay + 10;
        else this.holdLeft = canvas.horizontalRepeatDelay;
        if (!this.moveLeft()) {
          this.holdLeft = 1;
        }
      } else {
        this.holdLeft--;
      }
    } else {
      this.holdLeft = 0;
    }

    if (bougeRight && !bougeLeft && !autoBouge) {
      if (this.holdRight < 2) {
        if (this.holdRight === 0)
          this.holdRight = canvas.verticalRepeatDelay + 10;
        else this.holdRight = canvas.verticalRepeatDelay;
        if (!this.moveRight()) {
          this.holdRight = 1;
        }
      } else {
        this.holdRight--;
      }
    } else {
      this.holdRight = 0;
    }
  }

  toString(): string {
    return "Process Key State";
  }
}
